import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

// Global function to get current user ID - will be set by UserContext
let getCurrentUserId: () => string = () => '00000000-0000-0000-0000-000000000001';
let getCurrentUserEmail: () => string = () => 'user@example.com';

export const setCurrentUserCallbacks = (
  getUserId: () => string, 
  getUserEmail: () => string
) => {
  getCurrentUserId = getUserId;
  getCurrentUserEmail = getUserEmail;
};

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth headers
    this.client.interceptors.request.use(
      async (config) => {
        // In Azure App Service with Easy Auth, authentication headers are automatically added
        // For local development with MSAL, we need to add the token manually
        
        // Add authentication headers from user context
        config.headers['x-ms-client-principal-id'] = getCurrentUserId();
        config.headers['x-ms-client-principal-name'] = getCurrentUserEmail();

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to login
          localStorage.removeItem('msal_access_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();